import React, { useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import trashIcon from "../../../assets/images/trash.svg";
import closeIcon from "../../../assets/images/closeIcon.svg";
import { ChevronUp, Edit2 } from "lucide-react";

// FormBuilder Sidebar

const DraggableElement = ({ type, label, icon }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "formElement",
    item: { type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className={`p-2  ${type !== "textarea" && "border-b border-black"}  cursor-move bg-white hover:bg-gray-50 
        ${isDragging ? "opacity-50" : "opacity-100"}`}
    >
      <div className="flex items-center gap-3">
        <img className="h-3 w-3" src={icon} alt={type} />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
    </div>
  );
};

export const FormElementsSidebar = ({ FORM_ELEMENTS }) => (
  <div className="w-48 ">
    <div className="h-fit  px-2  rounded-lg border border-black">
      {FORM_ELEMENTS.map((element) => (
        <DraggableElement
          key={element.type}
          type={element.type}
          label={element.label}
          icon={element.icon}
        />
      ))}
    </div>
  </div>
);

// FormBuilder Drop zone and elements
export const DropZone = ({
  formData,
  setFormData,
  handleDeleteFormItem,
  handleUpdateFormItem,
  handleAddOption,
  handleUpdateOptions,
  handleDeleteOptions,
  FORM_ELEMENTS,
}) => {
  const [, drop] = useDrop(() => ({
    accept: ["formElement", "question"],
    drop: (item, monitor) => {
      if (monitor.getItemType() === "formElement") {
        const formItem = {
          name: `${item.type}-${Date.now()}`,
          key: `${item.type}-${Date.now()}`,
          type: item.type,
          text: "",
          label: "",
          placeholder: "",
          required: false,
          acceptMultiple: true,
          values:
            item.type === "radio-group" || "checkbox-group" || item.type === "select"
              ? [
                  { label: "Option 1", value: "Option 1" },
                  { label: "Option 2", value: "Option 2" },
                ]
              : [],
        };
        setFormData((prev) => [...prev, formItem]);
      }
    },
  }));

  const moveQuestion = (dragIndex, hoverIndex) => {
    setFormData((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      const draggedItem = newQuestions[dragIndex];
      newQuestions.splice(dragIndex, 1);
      newQuestions.splice(hoverIndex, 0, draggedItem);
      return newQuestions;
    });
  };

  return (
    <div
      ref={drop}
      className="flex-1 min-h-[400px] p-4 bg-purple-100 rounded-lg border-2 border-dashed border-gray-300"
    >
      {formData.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 text-center">
            Drag and drop form elements here to start building your form
          </p>
        </div>
      ) : (
        formData.map((form, index) => (
          <DraggableQuestion
            key={index}
            form={form}
            moveQuestion={moveQuestion}
            handleDeleteFormItem={handleDeleteFormItem}
            handleUpdateFormItem={handleUpdateFormItem}
            handleAddOption={handleAddOption}
            handleUpdateOptions={handleUpdateOptions}
            handleDeleteOptions={handleDeleteOptions}
            FORM_ELEMENTS={FORM_ELEMENTS}
          />
        ))
      )}
    </div>
  );
};

const FormPreview = ({ form }) => {
  switch (form.type) {
    case "text":
      return (
        <input
          type={form.subtype || form.type}
          className="w-full p-2 border rounded"
          placeholder={form.placeholder || "Enter text"}
          readOnly
        />
      );
    case "textarea":
      return (
        <textarea
          className="w-full p-2 border rounded"
          placeholder={form.placeholder || "Enter long text"}
          readOnly
          rows={3}
        />
      );
    case "number":
      return (
        <input
          type="number"
          className="w-full p-2 border rounded"
          placeholder={form.placeholder || ""}
          readOnly
        />
      );
    case "file":
      return <input type="file" className="w-full p-2 border rounded" disabled />;
    case "checkbox-group":
      return (
        <div className="space-y-2">
          {form.values?.map((option, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input type="checkbox" className="form-checkbox" disabled />
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      );
    case "radio-group":
      return (
        <div className="space-y-2">
          {form.values?.map((option, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="radio"
                name={`radio-${form.key}`}
                className="form-radio"
                disabled
              />
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      );
    case "select":
      return (
        <select className="w-full p-2 border rounded" disabled>
          <option value="">{form.placeholder}</option>
          {form.values?.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    default:
      return null;
  }
};

const EditDrawer = ({
  form,
  onUpdate,
  onAddOption,
  onUpdateOptions,
  onDeleteOptions,
}) => {
  return (
    <div className="bg-gray-50 p-4 rounded-b-lg border-t">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`required-${form.key}`}
            checked={form.required || false}
            onChange={(e) => onUpdate({ ...form, required: e.target.checked })}
          />
          <label
            htmlFor={`required-${form.key}`}
            className="text-sm font-medium text-gray-700"
          >
            Required field
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Label
          </label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={form.label || ""}
            onChange={(e) => onUpdate({ ...form, label: e.target.value })}
            placeholder="Enter label"
          />
        </div>

        {form.type === "number" ||
        form.type === "radio-group" ||
        form.type === "checkbox-group" ? (
          ""
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Placeholder
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={form.placeholder || ""}
              onChange={(e) => onUpdate({ ...form, placeholder: e.target.value })}
              placeholder="Enter placeholder text"
            />
          </div>
        )}

        {(form.type === "checkbox-group" ||
          form.type === "radio-group" ||
          form.type === "select") && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Options
              </label>
              <button
                className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                onClick={() => onAddOption(form.key)}
              >
                Add Option
              </button>
            </div>
            <div className="space-y-2">
              {form.values?.map((option, idx) => (
                <div
                  key={`${form.key}-option-${idx}`}
                  className="flex items-center gap-2"
                >
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded text-sm"
                    value={option.value}
                    onChange={(e) =>
                      onUpdateOptions(form.key, idx, {
                        text: e.target.value,
                        value: e.target.value,
                      })
                    }
                  />
                  <button
                    onClick={() => onDeleteOptions(form.key, option.value)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <img
                      src={closeIcon}
                      width={16}
                      height={16}
                      alt="delete option"
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {form.type === "checkbox-group" && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`acceptMultiple-${form.key}`}
              checked={form.acceptMultiple || false}
              onChange={(e) =>
                onUpdate({ ...form, acceptMultiple: e.target.checked })
              }
            />
            <label
              htmlFor={`acceptMultiple-${form.key}`}
              className="text-sm font-medium text-gray-700"
            >
              Allow Multiple Selections
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

const DraggableQuestion = ({
  form,
  index,
  moveQuestion,
  handleDeleteFormItem,
  handleUpdateFormItem,
  handleAddOption,
  handleUpdateOptions,
  handleDeleteOptions,
  FORM_ELEMENTS,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: "question",
    item: { id: form.key, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "question",
    hover: (item, monitor) => {
      if (!monitor.isOver({ shallow: true })) return;
      if (item.index === index) return;
      moveQuestion(item.index, index);
      item.index = index;
    },
  });

  const elementType = FORM_ELEMENTS.find((el) => el.type === form.type);
  const typeLabel = elementType ? elementType.label : form.type;

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`bg-white mb-2 rounded-lg border border-gray-400 ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
    >
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2 mb-2">
          <label className="text-sm font-medium text-purple-600">
            {form.label || typeLabel}
            {form.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isEditing ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <Edit2 className="w-4 h-4 text-gray-600" />
              )}
            </button>
            <button
              onClick={() => handleDeleteFormItem(form.key)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <img height={12} width={16} src={trashIcon} alt="delete form item" />
            </button>
          </div>
        </div>

        <FormPreview form={form} />
      </div>

      {isEditing && (
        <EditDrawer
          form={form}
          onUpdate={handleUpdateFormItem}
          onAddOption={handleAddOption}
          onUpdateOptions={handleUpdateOptions}
          onDeleteOptions={handleDeleteOptions}
        />
      )}
    </div>
  );
};
